import { RiCloseLine, RiInformationLine } from 'react-icons/ri';
import { useShallow } from 'zustand/react/shallow';

import { formatTime } from '@/music/format-time';
import { useAudioPlayerStore } from '@/music/hooks/use-audio-player-store';
import { useGenerationInfoStore } from '@/music/hooks/use-generation-info-store';
import { PlayPauseButton } from './play-pause-button';

export const PlayerBar = () => {
  const { activeTrack, isPlaying, currentTime, duration, hasError } = useAudioPlayerStore(
    useShallow((state) => ({
      activeTrack: state.activeTrack,
      isPlaying: state.isPlaying,
      currentTime: state.currentTime,
      duration: state.duration,
      hasError: state.hasError,
    }))
  );
  const play = useAudioPlayerStore((state) => state.play);
  const pause = useAudioPlayerStore((state) => state.pause);
  const seek = useAudioPlayerStore((state) => state.seek);
  const stop = useAudioPlayerStore((state) => state.stop);
  const openInfo = useGenerationInfoStore((state) => state.open);

  if (!activeTrack) return null;

  const rangeStart = activeTrack.startTime ?? 0;
  const rangeEnd = activeTrack.endTime ?? duration;
  const rangeLength = rangeEnd - rangeStart;
  const progress = rangeLength > 0 ? ((currentTime - rangeStart) / rangeLength) * 100 : 0;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 rounded-2xl border bg-white shadow-lg shadow-black/5 duration-300 motion-reduce:animate-none">
      {!hasError && (
        <div className="px-4 pt-2">
          <input
            type="range"
            min={rangeStart}
            max={rangeEnd || 1}
            step={0.1}
            value={currentTime}
            disabled={rangeLength === 0}
            onChange={(event) => seek(Number(event.target.value))}
            aria-label="재생 위치"
            className="[&::-webkit-slider-thumb]:bg-foreground [&::-moz-range-thumb]:bg-foreground [&:focus-visible::-webkit-slider-thumb]:ring-ring h-4 w-full cursor-pointer appearance-none rounded-full focus-visible:outline-none disabled:cursor-default [&::-moz-range-thumb]:size-3 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-webkit-slider-thumb]:size-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&:focus-visible::-webkit-slider-thumb]:ring-2"
            style={{
              backgroundImage: `linear-gradient(to right, var(--foreground) ${progress}%, var(--border) ${progress}%)`,
              backgroundSize: '100% 4px',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
        </div>
      )}
      <div className={`flex items-center gap-3 px-3 pb-3 ${hasError ? 'pt-3' : 'pt-1'}`}>
        <PlayPauseButton
          isPlaying={isPlaying}
          disabled={hasError}
          onClick={() => (isPlaying ? pause() : play(activeTrack))}
        />
        <p className="min-w-0 grow truncate text-sm font-medium">{activeTrack.title}</p>
        {hasError ? (
          <p className="text-muted-foreground shrink-0 text-xs">음원을 재생할 수 없습니다</p>
        ) : (
          <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
            {formatTime(currentTime - rangeStart)} /{' '}
            {rangeLength === 0 ? '-:--' : formatTime(rangeLength)}
          </span>
        )}
        {activeTrack.generation && (
          <button
            onClick={() => openInfo(activeTrack)}
            aria-label="생성 정보"
            title="생성 정보"
            className="text-muted-foreground hover:bg-accent hover:text-foreground flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors"
          >
            <RiInformationLine className="text-lg" />
          </button>
        )}
        <button
          onClick={stop}
          aria-label="플레이어 닫기"
          title="플레이어 닫기"
          className="text-muted-foreground hover:bg-accent hover:text-foreground flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors"
        >
          <RiCloseLine className="text-lg" />
        </button>
      </div>
    </div>
  );
};
